// CorrectionClient.jsx - Version avec Tailwind CSS natif
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import {
  ClipboardCheck, Search, Loader2, AlertCircle, CheckCircle, XCircle,
  Users, FileQuestion, Save, Award, RefreshCw, MessageSquare,
  ChevronLeft, ChevronRight, ChevronDown, User, Mail
} from 'lucide-react';

// ============================================================
// 1. COMPOSANTS UTILITAIRES
// ============================================================
const StatCard = ({ title, value, icon: IconComponent, color }) => (
  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-black text-slate-800 mt-1">{value}</p>
      </div>
      <div className={`p-2.5 rounded-xl ${color}`}>
        {IconComponent && <IconComponent size={18} className="text-white" />}
      </div>
    </div>
  </div>
);

const STATUT_BADGES = {
  REUSSI: 'bg-green-100 text-green-700',
  ECHEC: 'bg-red-100 text-red-700',
  EN_ATTENTE_CORRECTION: 'bg-amber-100 text-amber-700',
  NOT_STARTED: 'bg-slate-100 text-slate-500',
};

const StatutBadge = ({ statut }) => (
  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${STATUT_BADGES[statut] || 'bg-slate-100 text-slate-600'}`}>
    {(statut || 'N/A').replace(/_/g, ' ')}
  </span>
);

// ============================================================
// 2. COMPOSANT PRINCIPAL
// ============================================================
export default function CorrectionClient() {
  const { token, user, isAuthenticated } = useAuth();
  const correcteurId = user?.id;

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  }), [token]);

  // --- Type d'évaluation : 'classic' ou 'rich' ---
  const [evalType, setEvalType] = useState('classic');

  // --- Évaluations ---
  const [evaluations, setEvaluations] = useState([]);
  const [evaluationId, setEvaluationId] = useState('');
  const [loadingEvaluations, setLoadingEvaluations] = useState(true);

  // --- Étudiants ayant répondu ---
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalPages: 0, totalElements: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingStudents, setLoadingStudents] = useState(false);

  // --- Stats de l'évaluation sélectionnée ---
  const [statsEval, setStatsEval] = useState(null);

  // --- Questions (schéma) + réponse de l'étudiant sélectionné ---
  const [questions, setQuestions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [reponse, setReponse] = useState(null);
  const [loadingReponse, setLoadingReponse] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savingIndex, setSavingIndex] = useState(null);

  const [finalizing, setFinalizing] = useState(false);
  const [finalResult, setFinalResult] = useState(null);

  const [commentaireInscription, setCommentaireInscription] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  const [error, setError] = useState(null);

  // ============================================================
  // 3. CHARGEMENT DES ÉVALUATIONS (selon le type)
  // ============================================================
  useEffect(() => {
    if (!token || !isAuthenticated) return;
    const endpoint = evalType === 'rich'
      ? '/api/v1/client/rich/evaluations'
      : '/api/v1/client/evaluations';
    setLoadingEvaluations(true);
    fetch(`${APP_CONFIG.BASE_URL}${endpoint}`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = data.content || data.data || data || [];
        setEvaluations(list);
        // Réinitialiser la sélection
        setEvaluationId('');
        setSelectedStudent(null);
        setReponse(null);
        setFinalResult(null);
        setQuestions([]);
        setStudents([]);
        setStatsEval(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingEvaluations(false));
  }, [token, isAuthenticated, authHeaders, evalType]);

  // ============================================================
  // 4. CHARGEMENT DES ÉTUDIANTS + STATS POUR L'ÉVALUATION CHOISIE
  // ============================================================
  const fetchStudents = useCallback(async (page = 0) => {
    if (!evaluationId) return;
    setLoadingStudents(true);
    setError(null);
    try {
      const endpoint = evalType === 'rich'
        ? `/api/v1/client/rich/evaluations/${evaluationId}/etudiants-reponses`
        : `/api/v1/client/evaluations/${evaluationId}/etudiants-reponses`;
      const res = await fetch(
        `${APP_CONFIG.BASE_URL}${endpoint}?page=${page}&size=${pagination.size}`,
        { headers: authHeaders }
      );
      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
      const data = await res.json();
      const content = data.content || data.data || data || [];
      setStudents(Array.isArray(content) ? content : []);
      setPagination((prev) => ({
        ...prev,
        page: data.number ?? data.page ?? page,
        totalPages: data.totalPages || 0,
        totalElements: data.totalElements || content.length,
      }));
    } catch (err) {
      console.error('Erreur récupération étudiants avec réponses:', err);
      setError(err.message);
    } finally {
      setLoadingStudents(false);
    }
  }, [evaluationId, pagination.size, authHeaders, evalType]);

  const fetchStats = useCallback(async () => {
    if (!evaluationId) return;
    try {
      const endpoint = evalType === 'rich'
        ? `/api/v1/client/rich/evaluations/${evaluationId}/resultats`
        : `/api/v1/client/evaluations/${evaluationId}/resultats`;
      const res = await fetch(`${APP_CONFIG.BASE_URL}${endpoint}`, { headers: authHeaders });
      if (res.ok) setStatsEval(await res.json());
    } catch (err) {
      console.warn('Stats évaluation indisponibles', err);
    }
  }, [evaluationId, authHeaders, evalType]);

  const fetchQuestions = useCallback(async () => {
    if (!evaluationId) return;
    try {
      const endpoint = evalType === 'rich'
        ? `/api/v1/client/rich/evaluations/${evaluationId}/questions`
        : `/api/v1/client/evaluations/${evaluationId}/questions`;
      const res = await fetch(`${APP_CONFIG.BASE_URL}${endpoint}`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setQuestions(data.questions || []);
      }
    } catch (err) {
      console.warn('Questions indisponibles', err);
    }
  }, [evaluationId, authHeaders, evalType]);

  useEffect(() => {
    setSelectedStudent(null);
    setReponse(null);
    setFinalResult(null);
    setPagination((prev) => ({ ...prev, page: 0 }));
    if (evaluationId) {
      fetchStudents(0);
      fetchStats();
      fetchQuestions();
    } else {
      setStudents([]);
      setStatsEval(null);
      setQuestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluationId, evalType]);

  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) return students;
    const q = searchTerm.trim().toLowerCase();
    return students.filter((s) => {
      const nom = `${s.etudiantPrenom || s.etudiant?.prenom || ''} ${s.etudiantNom || s.etudiant?.nom || ''}`.toLowerCase();
      const email = (s.etudiantEmail || s.etudiant?.email || '').toLowerCase();
      return nom.includes(q) || email.includes(q);
    });
  }, [students, searchTerm]);

  // ============================================================
  // 5. SÉLECTION D'UN ÉTUDIANT -> CHARGEMENT DE SA COPIE
  // ============================================================
  const selectStudent = useCallback(async (student) => {
    setSelectedStudent(student);
    setFinalResult(null);
    setCommentaireInscription(student.commentaireClient || '');
    const etudiantId = student.etudiantId || student.etudiant?.id;
    if (!etudiantId || !evaluationId) return;

    setLoadingReponse(true);
    setError(null);
    try {
      const endpoint = evalType === 'rich'
        ? `/api/v1/client/rich/reponses/${etudiantId}/${evaluationId}`
        : `/api/v1/client/reponses/${etudiantId}/${evaluationId}`;
      const res = await fetch(`${APP_CONFIG.BASE_URL}${endpoint}`, { headers: authHeaders });
      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
      const data = await res.json();
      setReponse(data);
      const initialDrafts = {};
      (data?.details || []).forEach((d) => {
        initialDrafts[d.questionIndex] = {
          points: d.pointsObtenus != null ? String(d.pointsObtenus) : '',
          appreciation: d.appreciation || '',
        };
      });
      setDrafts(initialDrafts);
    } catch (err) {
      console.error('Erreur récupération réponse:', err);
      setError(err.message);
    } finally {
      setLoadingReponse(false);
    }
  }, [evaluationId, authHeaders, evalType]);

  const questionByIndex = (idx) => questions[idx] || {};

  const updateDraft = (idx, field, value) => {
    setDrafts((prev) => ({ ...prev, [idx]: { ...prev[idx], [field]: value } }));
  };

  // ============================================================
  // 6. CORRIGER UNE QUESTION
  // ============================================================
  const saveQuestion = async (detail) => {
    const etudiantId = selectedStudent.etudiantId || selectedStudent.etudiant?.id;
    const draft = drafts[detail.questionIndex] || {};
    const points = parseFloat(draft.points);

    if (Number.isNaN(points)) {
      setError('Veuillez saisir une note valide pour cette question.');
      return;
    }
    if (detail.pointsPossibles != null && points > Number(detail.pointsPossibles)) {
      setError(`La note ne peut pas dépasser ${detail.pointsPossibles} points pour cette question.`);
      return;
    }

    setSavingIndex(detail.questionIndex);
    setError(null);
    try {
      const endpoint = evalType === 'rich'
        ? `/api/v1/client/rich/reponses/${etudiantId}/${evaluationId}/corriger-question`
        : `/api/v1/client/reponses/${etudiantId}/${evaluationId}/question`;
      const res = await fetch(
        `${APP_CONFIG.BASE_URL}${endpoint}`,
        {
          method: 'PATCH',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            questionIndex: detail.questionIndex,
            pointsObtenus: points,
            appreciation: draft.appreciation || '',
          }),
        }
      );
      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
      setReponse((prev) => ({
        ...prev,
        details: prev.details.map((d) =>
          d.questionIndex === detail.questionIndex
            ? { ...d, pointsObtenus: points, appreciation: draft.appreciation, estCorrigeClient: true }
            : d
        ),
      }));
    } catch (err) {
      console.error('Erreur correction question:', err);
      setError(err.message);
    } finally {
      setSavingIndex(null);
    }
  };

  const toutesLesQuestionsCorrigees = useMemo(() => {
    if (!reponse?.details?.length) return false;
    return reponse.details.every((d) => d.pointsObtenus != null);
  }, [reponse]);

  // ============================================================
  // 7. FINALISER LA CORRECTION
  // ============================================================
  const finaliserCorrection = async () => {
    const etudiantId = selectedStudent.etudiantId || selectedStudent.etudiant?.id;
    setFinalizing(true);
    setError(null);
    try {
      const endpoint = evalType === 'rich'
        ? `/api/v1/client/rich/evaluations/${evaluationId}/finaliser-correction`
        : `/api/v1/client/evaluations/${evaluationId}/finaliser-correction`;
      const res = await fetch(
        `${APP_CONFIG.BASE_URL}${endpoint}?etudiantId=${etudiantId}&correcteurId=${correcteurId}`,
        { method: 'POST', headers: authHeaders }
      );
      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
      const result = await res.json();
      setFinalResult(result);
      fetchStudents(pagination.page);
      fetchStats();
    } catch (err) {
      console.error('Erreur finalisation correction:', err);
      setError(err.message);
    } finally {
      setFinalizing(false);
    }
  };

  // ============================================================
  // 8. COMMENTAIRE SUR L'INSCRIPTION
  // ============================================================
  const saveCommentaire = async () => {
    const inscriptionId = selectedStudent.id || selectedStudent.inscriptionId;
    if (!inscriptionId) return;
    setSavingComment(true);
    setError(null);
    try {
      const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/inscriptions/${inscriptionId}/commentaire`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentaire: commentaireInscription }),
      });
      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
    } catch (err) {
      console.error('Erreur enregistrement commentaire:', err);
      setError(err.message);
    } finally {
      setSavingComment(false);
    }
  };

  if (!isAuthenticated || !token) {
    return (
      <div className="p-6 text-center">
        <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
        <p className="text-gray-700 font-medium">Session expirée, veuillez vous reconnecter.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 bg-slate-50 min-h-screen font-sans">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tighter flex items-center gap-3">
            <ClipboardCheck size={28} className="text-green-600" />
            Correction des évaluations
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Corrigez les réponses des étudiants et finalisez leurs résultats
          </p>
        </div>
        <button
          onClick={() => { fetchStudents(pagination.page); fetchStats(); }}
          disabled={!evaluationId || loadingStudents}
          className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white transition text-sm flex items-center gap-1 disabled:opacity-40"
        >
          <RefreshCw size={14} className={loadingStudents ? 'animate-spin' : ''} /> Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><AlertCircle size={14} /> {error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      {/* Sélecteur de type d'évaluation */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col md:flex-row gap-3 items-start md:items-center">
        <div className="flex items-center gap-2 text-sm text-slate-600 font-semibold">
          <FileQuestion size={16} className="text-green-600" /> Type :
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEvalType('classic')}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
              evalType === 'classic'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Classique
          </button>
          <button
            onClick={() => setEvalType('rich')}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
              evalType === 'rich'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Enrichie
          </button>
        </div>
      </div>

      {/* Sélecteur d'évaluation */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col md:flex-row gap-3 items-start md:items-center">
        <div className="flex items-center gap-2 text-sm text-slate-600 font-semibold">
          <FileQuestion size={16} className="text-green-600" /> Évaluation :
        </div>
        <div className="relative flex-1 min-w-[260px]">
          <select
            value={evaluationId}
            onChange={(e) => setEvaluationId(e.target.value)}
            disabled={loadingEvaluations}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm appearance-none bg-white"
          >
            <option value="">— Sélectionner une évaluation —</option>
            {evaluations.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.titre} {ev.code ? `(${ev.code})` : ''}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {evaluationId && (
        <>
          {/* Stats de l'évaluation */}
          {statsEval && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Participants" value={statsEval.totalParticipants ?? 0} icon={Users} color="bg-blue-500" />
              <StatCard title="Note moyenne" value={statsEval.noteMoyenne != null ? `${statsEval.noteMoyenne}%` : '—'} icon={Award} color="bg-green-500" />
              <StatCard title="Min / Max" value={`${statsEval.noteMin ?? '—'} / ${statsEval.noteMax ?? '—'}`} icon={ClipboardCheck} color="bg-purple-500" />
              <StatCard title="Participation" value={statsEval.tauxParticipation != null ? `${statsEval.tauxParticipation}%` : '—'} icon={CheckCircle} color="bg-amber-500" />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Colonne gauche : liste des étudiants */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 lg:col-span-1">
              <div className="relative mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher un étudiant…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                />
              </div>

              {loadingStudents ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-green-600" size={28} />
                </div>
              ) : filteredStudents.length === 0 ? (
                <p className="text-xs text-slate-400 italic text-center py-6">Aucun étudiant n'a encore répondu.</p>
              ) : (
                <div className="space-y-2 max-h-[520px] overflow-y-auto">
                  {filteredStudents.map((s) => {
                    const etudiantId = s.etudiantId || s.etudiant?.id;
                    const isActive = selectedStudent && (selectedStudent.etudiantId || selectedStudent.etudiant?.id) === etudiantId;
                    return (
                      <button
                        key={s.id || etudiantId}
                        onClick={() => selectStudent(s)}
                        className={`w-full text-left p-3 rounded-xl border transition ${
                          isActive ? 'bg-green-600 border-green-600 text-white' : 'bg-slate-50 border-slate-100 hover:border-green-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 font-semibold text-sm">
                          <User size={13} className={isActive ? 'text-white' : 'text-slate-400'} />
                          {s.etudiantPrenom || s.etudiant?.prenom} {s.etudiantNom || s.etudiant?.nom}
                        </div>
                        <div className={`flex items-center gap-1 text-[11px] mt-0.5 ${isActive ? 'text-green-100' : 'text-slate-400'}`}>
                          <Mail size={11} /> {s.etudiantEmail || s.etudiant?.email}
                        </div>
                        <div className="mt-1.5"><StatutBadge statut={s.statut} /></div>
                      </button>
                    );
                  })}
                </div>
              )}

              {pagination.totalPages > 1 && (
                <div className="flex justify-between items-center pt-3 mt-3 border-t border-gray-100">
                  <span className="text-[11px] text-gray-500">Page {pagination.page + 1}/{pagination.totalPages}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => fetchStudents(pagination.page - 1)}
                      disabled={pagination.page === 0}
                      className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 text-sm"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={() => fetchStudents(pagination.page + 1)}
                      disabled={pagination.page + 1 >= pagination.totalPages}
                      className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 text-sm"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Colonne droite : copie de l'étudiant sélectionné */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 lg:col-span-2">
              {!selectedStudent && (
                <div className="text-center py-16 text-slate-400">
                  <ClipboardCheck size={40} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Sélectionnez un étudiant pour afficher sa copie</p>
                </div>
              )}

              {selectedStudent && loadingReponse && (
                <div className="flex justify-center py-16">
                  <Loader2 className="animate-spin text-green-600" size={32} />
                </div>
              )}

              {selectedStudent && reponse && !loadingReponse && (
                <>
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                      <User size={16} className="text-green-600" />
                      {selectedStudent.etudiantPrenom || selectedStudent.etudiant?.prenom}{' '}
                      {selectedStudent.etudiantNom || selectedStudent.etudiant?.nom}
                    </h3>
                    {reponse.estCorrigeGlobal && (
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                        <CheckCircle size={11} /> Correction finalisée
                      </span>
                    )}
                  </div>

                  <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                    {(reponse.details || []).map((detail) => {
                      const q = questionByIndex(detail.questionIndex);
                      const draft = drafts[detail.questionIndex] || {};
                      const isAuto = detail.estCorrigeClient && q.feedbackAuto;
                      return (
                        <div key={detail.questionIndex} className="bg-slate-50 rounded-xl border border-slate-100 p-3">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="text-sm text-slate-700">
                              <strong>Q{detail.questionIndex + 1}.</strong> {q.enonce || '(énoncé indisponible)'}
                            </p>
                            {isAuto && (
                              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                                Auto-corrigé
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Réponse de l'étudiant</p>
                              <p className="text-xs text-slate-700 mt-0.5">{formatReponse(detail.reponseCandidat)}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">Solution attendue</p>
                              <p className="text-xs text-slate-700 mt-0.5">{detail.solution || '—'}</p>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-end gap-2">
                            <div>
                              <label className="block text-[10px] text-gray-500 mb-1">
                                Points ({detail.pointsPossibles ?? '?'} max)
                              </label>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                max={detail.pointsPossibles ?? undefined}
                                value={draft.points ?? ''}
                                disabled={isAuto}
                                onChange={(e) => updateDraft(detail.questionIndex, 'points', e.target.value)}
                                className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                              />
                            </div>
                            <div className="flex-1 min-w-[180px]">
                              <label className="block text-[10px] text-gray-500 mb-1">Appréciation</label>
                              <input
                                type="text"
                                value={draft.appreciation ?? ''}
                                disabled={isAuto}
                                onChange={(e) => updateDraft(detail.questionIndex, 'appreciation', e.target.value)}
                                placeholder="Commentaire sur cette réponse…"
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                              />
                            </div>
                            {!isAuto && (
                              <button
                                onClick={() => saveQuestion(detail)}
                                disabled={savingIndex === detail.questionIndex}
                                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm flex items-center gap-1 disabled:opacity-50"
                              >
                                {savingIndex === detail.questionIndex ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <Save size={14} />
                                )}
                                Enregistrer
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
                    <button
                      onClick={finaliserCorrection}
                      disabled={!toutesLesQuestionsCorrigees || finalizing}
                      className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {finalizing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                      Finaliser la correction
                    </button>
                    {!toutesLesQuestionsCorrigees && (
                      <span className="text-xs text-slate-400">
                        Toutes les questions doivent être notées avant de finaliser.
                      </span>
                    )}
                  </div>

                  {finalResult && (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                      {finalResult.reussi ? (
                        <CheckCircle size={22} className="text-green-600" />
                      ) : (
                        <XCircle size={22} className="text-red-500" />
                      )}
                      <div className="text-sm">
                        <strong>Résultat final : {finalResult.score}%</strong> —{' '}
                        {finalResult.reussi ? 'Réussi' : 'Échec'} ({finalResult.bonnesReponses}/{finalResult.totalQuestions})
                      </div>
                    </div>
                  )}

                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <MessageSquare size={12} /> Commentaire sur l'inscription
                    </p>
                    <textarea
                      value={commentaireInscription}
                      onChange={(e) => setCommentaireInscription(e.target.value)}
                      rows={3}
                      placeholder="Remarque générale sur le parcours de cet étudiant…"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <button
                      onClick={saveCommentaire}
                      disabled={savingComment}
                      className="mt-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      {savingComment ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      Enregistrer le commentaire
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatReponse(value) {
  if (value == null) return '—';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}